import { defineComponent as _defineComponent } from 'vue'
import {
  Annotation,
  AppliedAnnotation,
  Comment,
  HandgradingResult,
} from "ag-client-typescript";

import ContextMenu from "@/components/context_menu/context_menu.vue";
import ContextMenuItem from "@/components/context_menu/context_menu_item.vue";
import Modal from "@/components/modal.vue";
import ProgressBar from "@/components/progress_bar.vue";
import { new_handle_global_errors_async } from "@/error_handling";
import { SafeMap } from "@/safe_map";
import { assert_not_null, chain, toggle } from "@/utils";

import {
  handgrading_comment_factory,
  HandgradingComment,
} from "../project_view/handgrading/handgrading_comment";

import { CODE_THEME_STORE } from "./code_theme_store";
import { nextTick } from "process";
import { LineSelector, useLineSelector } from "./line_selector";
import { computed, onMounted, reactive, readonly, ref, watch } from "vue";
import hljs from "highlight.js";

type PropTypes = {
  file_contents: Promise<string>;
  filename?: string;
  progress?: number | null;
  display_size_threshold?: number;
  view_file_height?: string;
  view_file_max_height?: string;
  handgrading_result?: HandgradingResult;
  enable_custom_comments?: boolean;
  readonly_handgrading_results?: boolean;
  is_code_file?: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'view_file',
  props: {
    file_contents: null,
    filename: { default: "" },
    progress: { default: null },
    display_size_threshold: { default: Math.pow(10, 6) },
    view_file_height: { default: "" },
    view_file_max_height: { default: "" },
    handgrading_result: null,
    enable_custom_comments: { type: Boolean, default: false },
    readonly_handgrading_results: { type: Boolean, default: true },
    is_code_file: { type: Boolean, default: false }
  },
  setup(__props: any, { expose }) {

const props = __props as { file_contents: Promise<string>, filename: string, progress: number | null, display_size_threshold: number, view_file_height: string, view_file_max_height: string, handgrading_result?: HandgradingResult, enable_custom_comments: boolean, readonly_handgrading_results: boolean, is_code_file: boolean };



const num_lines_per_page = 1000;

let selector = ref<LineSelector | null>(null);

// Apparently we need to alias props.handgrading_result for deep reactivity on
// its members.
// We can possibly simplify this once we're fully migrated to Vue 3.
const d_handgrading_result = ref<HandgradingResult | null>(null);

const state = reactive({
  d_file_contents: "",
  d_loading: false,
  d_saving: false,
  d_show_anyway: false,

  d_num_lines_rendered: num_lines_per_page,

  // Tracking file copying
  d_is_file_hovered: false,
  d_is_file_copying: false,

  d_hovered_comment: null as HandgradingComment | null,

  d_context_menu_is_open: false,
  d_context_menu_coordinates: { x: 0, y: 0 },
  d_show_comment_modal: false,
  d_comment_text: "",

  d_selection_announcement: "",
});

watch(
  () => props.handgrading_result,
  (handgrading_result) => {
    if (handgrading_result !== undefined) {
      d_handgrading_result.value = handgrading_result;
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => props.file_contents,
  new_handle_global_errors_async((new_content: Promise<string>) => {
    return toggle(state, "d_loading", async () => {
      state.d_show_anyway = false;
      state.d_file_contents = await new_content;
    });
  }),
  { immediate: true },
);

const is_code_theme_dark = computed(() => {
  return CODE_THEME_STORE.current_code_theme === "dark";
});

const file_is_large = computed(() => {
  return state.d_file_contents.length > props.display_size_threshold;
});

// IMPORTANT: We want this to be a computed property. Indexing into
// a large reactive array in the template will significantly increase
// render times.
const split_content = computed(() => {
  return state.d_file_contents.split("\n");
});

// Makes each line of code have independent styling by padding newlines in
// the midle of spans.
function separate_span_tags_with_newlines(code_html_str: string): string {
  // Adapted from: https://stackoverflow.com/questions/64280814
  //  /how-can-i-correctly-highlight-a-line-by-line-code-using-highlight-js-react
  const open_spans: string[] = [];

  const padded_code = code_html_str.replace(
    /(<span [^>]+>)|(<\/span>)|(\n)/g,
    (match) => {
      if (match === "\n") {
        return "</span>".repeat(open_spans.length) + "\n" + open_spans.join("");
      }

      if (match === "</span>") {
        open_spans.pop();
      } else {
        open_spans.push(match);
      }

      return match;
    },
  );

  return padded_code;
}

// Returns HTML for highlighted contents of code file, split by newlines.
const split_code_content = computed(() => {
  const highlighted_code = hljs.highlightAuto(state.d_file_contents).value;
  const padded_highlighted_code =
    separate_span_tags_with_newlines(highlighted_code);
  return padded_highlighted_code.split("\n");
});

const copy_file_to_clipboard = new_handle_global_errors_async(async () => {
  await navigator.clipboard.writeText(state.d_file_contents);
  state.d_is_file_copying = true;

  // Wait to set icon back
  setTimeout(() => {
    state.d_is_file_copying = false;
  }, 3000);
});

const num_lines_to_show = computed(() => {
  return Math.min(state.d_num_lines_rendered, split_content.value.length);
});

function render_more_lines() {
  state.d_num_lines_rendered = Math.min(
    split_content.value.length,
    state.d_num_lines_rendered + num_lines_per_page,
  );
}

const handgrading_enabled = computed(() => {
  return props.handgrading_result !== undefined;
});

const can_edit_handgrading = computed(() => {
  return handgrading_enabled.value && !props.readonly_handgrading_results;
});

const handgrading_comments = computed<SafeMap<number, HandgradingComment[]>>(
  () => {
    if (d_handgrading_result.value === null) {
      return new SafeMap();
    }

    let result = new SafeMap<number, HandgradingComment[]>();

    let annotations = d_handgrading_result.value.applied_annotations.filter(
      (item) => item.location.filename === props.filename,
    );

    let comments = d_handgrading_result.value.comments.filter(
      (item) =>
        item.location !== null && item.location.filename === props.filename,
    );

    for (let item of chain<AppliedAnnotation | Comment>(
      annotations,
      comments,
    )) {
      let handgrading_comment = handgrading_comment_factory(item);
      result
        .get(handgrading_comment.location.last_line, [], true)
        .push(handgrading_comment);
    }

    // Sort lists of comments ending on the same line by first line
    for (let [last_line, comment_list] of result) {
      comment_list.sort(
        (first, second) =>
          first.location.first_line - second.location.first_line,
      );
    }

    return result;
  },
);

function comment_is_deletable(comment: HandgradingComment): boolean {
  return (
    can_edit_handgrading.value &&
    (props.enable_custom_comments || !comment.is_custom)
  );
}

const delete_handgrading_comment = new_handle_global_errors_async(
  async (handgrading_comment: HandgradingComment) => {
    if (!state.d_saving) {
      await toggle(state, "d_saving", async () => {
        await handgrading_comment.delete();
        state.d_hovered_comment = null;
      });
    }
  },
);

// Returns true if line_num is contained in any provided handgrading comments.
function line_in_comment(line_num: number) {
  for (let [last_line, comment_list] of handgrading_comments.value) {
    let first_line = comment_list[0].location.first_line;
    if (line_num >= first_line && line_num <= last_line) {
      return true;
    }
  }
  return false;
}

function start_mouse_selection(clicked_line_index: number) {
  if (
    // IMPORTANT: CHANGE THESE CHECKS TOGETHER
    // Note: Don't refactor this and similar checks unless
    // you have a very good reason.
    !can_edit_handgrading.value ||
    // Don't interrupt existing mouse selection
    selector.value?.controls === "mouse" ||
    state.d_context_menu_is_open ||
    state.d_saving
  ) {
    return;
  }

  selector.value = useLineSelector(clicked_line_index, "mouse");
}

function update_mouse_selection(hovered_line_index: number) {
  if (
    // IMPORTANT: CHANGE THESE CHECKS TOGETHER
    // Note: Don't refactor this and similar checks unless
    // you have a very good reason.
    !can_edit_handgrading.value ||
    // Don't interrupt keyboard selection
    selector.value?.controls === "keyboard" ||
    state.d_context_menu_is_open ||
    state.d_saving
  ) {
    return;
  }

  selector.value?.update_selection(hovered_line_index);
}

function commit_mouse_selection(mouseup_event: MouseEvent) {
  if (
    // IMPORTANT: CHANGE THESE CHECKS TOGETHER
    // Note: Don't refactor this and similar checks unless
    // you have a very good reason.
    !can_edit_handgrading.value ||
    // It's possible to cancel a mouse selection with esc before
    // committing it.
    // If that happens, this method should do nothing.
    selector.value === null ||
    // Don't interrupt keyboard selection
    selector.value.controls === "keyboard" ||
    state.d_context_menu_is_open ||
    state.d_saving
  ) {
    return;
  }

  open_annotation_context_menu({
    x: mouseup_event.clientX,
    y: mouseup_event.clientY,
  });
}

// There is overlap between how cancellation is triggered for keyboard and mouse.
// See cancel_commenting for a method that handles all possibilities.

function start_or_expand_keyboard_selection(
  direction: "up" | "down",
  from_line_index: number,
) {
  if (
    // IMPORTANT: CHANGE THESE CHECKS TOGETHER
    // Note: Don't refactor this and similar checks unless
    // you have a very good reason.
    !can_edit_handgrading.value ||
    // Don't interrupt mouse selection
    selector.value?.controls === "mouse" ||
    state.d_context_menu_is_open ||
    state.d_saving
  ) {
    return;
  }

  if (selector.value === null) {
    selector.value = useLineSelector(from_line_index, "keyboard");
  }

  let new_line: number;
  switch (direction) {
    case "down":
      if (selector.value.range.first < selector.value.anchor_index) {
        new_line = selector.value.range.first + 1;
      } else {
        new_line = selector.value.range.last + 1;
      }
      break;
    case "up":
      if (selector.value.range.last > selector.value.anchor_index) {
        new_line = selector.value.range.last - 1;
      } else {
        new_line = selector.value.range.first - 1;
      }
      break;
  }

  if (new_line < 0 || new_line >= num_lines_to_show.value) {
    return;
  }
  selector.value.update_selection(new_line);

  state.d_selection_announcement =
    `Selecting lines ${selector.value.range.first + 1} ` +
    `to ${selector.value.range.last + 1}`;

  // Note: we can keep focus on the original anchor because that
  // will make it visually apparent to the user what the anchor is
  // and simplify the cancel and post-commit behaviors.
}

function commit_keyboard_selection(line_index: number) {
  if (
    // IMPORTANT: CHANGE THESE CHECKS TOGETHER
    // Note: Don't refactor this and similar checks unless
    // you have a very good reason.
    !can_edit_handgrading.value ||
    // Don't interrupt mouse selection
    selector.value?.controls === "mouse" ||
    state.d_context_menu_is_open ||
    state.d_saving
  ) {
    return;
  }

  // If there isn't a current selection, start one on the current line,
  // then immediately commit it.
  if (selector.value === null) {
    selector.value = useLineSelector(line_index, "keyboard");
  }

  const last_line_elt = get_line_element_at(selector.value.range.last);
  const bounding_rect = last_line_elt.getBoundingClientRect();

  open_annotation_context_menu({
    x: bounding_rect.x,
    y: bounding_rect.y,
  });
}

function open_annotation_context_menu(menu_coordinates: {
  x: number;
  y: number;
}) {
  state.d_context_menu_coordinates = menu_coordinates;
  state.d_context_menu_is_open = true;
}

const line_shortcuts = computed(() => {
  if (!can_edit_handgrading.value) {
    return undefined;
  }
  return "Enter Shift+ArrowUp Shift+ArrowDown Escape";
});

const apply_annotation = new_handle_global_errors_async(
  async (annotation: Annotation) => {
    assert_not_null(props.filename);
    return toggle(state, "d_saving", async () => {
      assert_not_null(selector.value);
      await AppliedAnnotation.create(d_handgrading_result.value!.pk, {
        annotation: annotation.pk,
        location: {
          first_line: selector.value.range.first,
          last_line: selector.value.range.last,
          filename: props.filename,
        },
      });
      finish_commenting();
    });
  },
);

const comment_text = ref<HTMLElement>();

function open_comment_modal() {
  state.d_context_menu_is_open = false;
  state.d_show_comment_modal = true;
  nextTick(() => comment_text.value?.focus());
}

const create_comment = new_handle_global_errors_async(() => {
  return toggle(state, "d_saving", async () => {
    assert_not_null(selector.value);
    assert_not_null(props.filename);
    await Comment.create(d_handgrading_result.value!.pk, {
      text: state.d_comment_text,
      location: {
        first_line: selector.value.range.first,
        last_line: selector.value.range.last,
        filename: props.filename,
      },
    });
    finish_commenting();
    state.d_show_comment_modal = false;
    state.d_comment_text = "";
  });
});

function finish_commenting() {
  // IMPORTANT: If you change anything about this method,
  // double check whether cancel_commenting needs the same changes.
  // cancel_commenting is currently an alias for this method.
  state.d_context_menu_is_open = false;
  state.d_selection_announcement = "";

  assert_not_null(selector.value);
  const last_index = selector.value.range.last;

  nextTick(() => {
    focus_line(last_index);
    selector.value = null;
  });
}

function cancel_commenting() {
  if (selector.value === null) {
    return;
  }

  const anchor_index = selector.value.anchor_index;

  if (state.d_context_menu_is_open) {
    state.d_context_menu_is_open = false;
    nextTick(() => {
      focus_line(anchor_index);
    });
  } else {
    focus_line(anchor_index);
  }

  if (selector.value?.controls === "keyboard") {
    state.d_selection_announcement = "Selection cancelled";
  }

  selector.value = null;
}

function focus_line(line_index: number) {
  get_line_element_at(line_index)?.focus();
}

const code_lines = ref<HTMLElement[]>([]);

function get_line_element_at(line_index: number) {
  return code_lines.value[line_index];
}

expose({
  state,
  d_handgrading_result,
  d_selector: selector.value,
  is_loading: () => state.d_loading,
  get_props: () => props,
});

return { __sfc: true,props, num_lines_per_page, selector, d_handgrading_result, state, is_code_theme_dark, file_is_large, split_content, separate_span_tags_with_newlines, split_code_content, copy_file_to_clipboard, num_lines_to_show, render_more_lines, handgrading_enabled, can_edit_handgrading, handgrading_comments, comment_is_deletable, delete_handgrading_comment, line_in_comment, start_mouse_selection, update_mouse_selection, commit_mouse_selection, start_or_expand_keyboard_selection, commit_keyboard_selection, open_annotation_context_menu, line_shortcuts, apply_annotation, comment_text, open_comment_modal, create_comment, finish_commenting, cancel_commenting, focus_line, code_lines, get_line_element_at, ContextMenu, ContextMenuItem, Modal, ProgressBar }
}

})